import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { Activity } from "../../types";
import { ActivityContext } from "../../contexts/ActivityContext";
import { EmbeddedMapView, RouteData } from "../Map/EmbeddedMapView";
import {
  getActivityStorageService,
  TrackPoint,
} from "../../services/activityStorageService";
import { getRouteService } from "../../services/routeService";
import { MapService } from "../../services/mapService";
import { getOverpassWorkerService } from "../../services/overpassWorkerService";
import { usePreferences } from "../../contexts/PreferencesContext";
import {
  formatDistanceWithUnit,
  formatDurationShort,
  formatSpeedWithUnit,
  formatDateTime,
} from "../../utils/formatting";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { ActivityContextMenu } from "./ActivityContextMenu";
import debugx from "debugx";

const debug = debugx("bikedash:activities");

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  activity: Activity | null;
  previousActivity: Activity | null;
  nextActivity: Activity | null;
  activityIndex: number;
}

interface ConfirmationState {
  visible: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  isDangerous?: boolean;
}

interface MergePreviewState {
  visible: boolean;
  firstActivity: Activity | null;
  firstTrackPoints: TrackPoint[];
  secondActivity: Activity | null;
  secondTrackPoints: TrackPoint[];
  isIntoActive: boolean;
  onConfirm: () => void;
}

// Separate component for total stats that includes live current activity
const TotalStats = React.memo(
  ({
    activities,
    formatDistance,
    formatDuration,
  }: {
    activities: Activity[];
    formatDistance: (meters: number) => string;
    formatDuration: (ms: number) => string;
  }) => {
    const activityContext = useContext(ActivityContext);
    const [, forceUpdate] = useState({});

    // Force update every second when activity is active to show live duration
    useEffect(() => {
      if (activityContext?.state !== "idle") {
        const interval = setInterval(() => {
          forceUpdate({});
        }, 1000);
        return () => clearInterval(interval);
      }
      return undefined;
    }, [activityContext?.state]);

    // Calculate base totals from activities list (fully memoized, excluding excluded activities)
    const baseTotals = useMemo(() => {
      const includedActivities = activities.filter((a) => !a.excluded);
      const count = includedActivities.length;
      const totalDistance = includedActivities.reduce(
        (sum, a) => sum + (a.distance || 0),
        0,
      );
      const totalDuration = includedActivities.reduce((sum, a) => {
        const duration =
          (a.endTime || a.startTime) - a.startTime - (a.pausedDuration || 0);
        return sum + duration;
      }, 0);
      return { count, distance: totalDistance, duration: totalDuration };
    }, [activities]);

    // Add current activity on top (not memoized, updates every render/second)
    let displayTotals = { ...baseTotals };

    if (
      activityContext?.state !== "idle" &&
      activityContext?.currentActivityId
    ) {
      const existingActivity = activities.find(
        (a) => a.id === activityContext.currentActivityId,
      );

      // Only add current activity to totals if it's not excluded
      if (!existingActivity?.excluded) {
        if (!existingActivity) {
          // Current activity is not in the list yet, add it
          displayTotals.count += 1;
          displayTotals.distance += activityContext.metrics.distance;
          displayTotals.duration += activityContext.getElapsedTime();
        } else {
          // Current activity is in the list, update with live values
          displayTotals.distance =
            displayTotals.distance -
            (existingActivity.distance || 0) +
            activityContext.metrics.distance;
          displayTotals.duration =
            displayTotals.duration -
            ((existingActivity.endTime || existingActivity.startTime) -
              existingActivity.startTime -
              (existingActivity.pausedDuration || 0)) +
            activityContext.getElapsedTime();
        }
      }
    }

    return (
      <>
        <div className="status-item">
          <div className="status-label">Activities</div>
          <div className="status-value">{displayTotals.count}</div>
        </div>
        <div className="status-item">
          <div className="status-label">Total Distance</div>
          <div className="status-value">
            {formatDistance(displayTotals.distance)}
          </div>
        </div>
        <div className="status-item">
          <div className="status-label">Total Time</div>
          <div className="status-value">
            {formatDuration(displayTotals.duration)}
          </div>
        </div>
      </>
    );
  },
);

// Separate component for just the map to prevent rerenders
const ActivityMap = React.memo(
  ({
    routes,
    onMapReady,
    onMapMoved,
  }: {
    routes: RouteData[];
    onMapReady: (fitFn: () => void) => void;
    onMapMoved: (isFitted: boolean) => void;
  }) => {
    return (
      <EmbeddedMapView
        routes={routes}
        fitToContent={true}
        interactive={true}
        onMapReady={onMapReady}
        onMapMoved={onMapMoved}
      />
    );
  },
);

// Separate component for activity list items to isolate rerenders
const ActivityListItem = React.memo(
  ({
    activity,
    onSelect,
    onDelete,
    onContextMenu,
    onStartRoute,
    formatDate,
    formatDistance,
    formatDuration,
    formatSpeed,
  }: {
    activity: Activity;
    onSelect: (activity: Activity) => void;
    onDelete: (activityId: string, activityTitle?: string) => void;
    onContextMenu: (
      e: React.MouseEvent | React.TouchEvent,
      activity: Activity,
    ) => void;
    onStartRoute: (activity: Activity) => void;
    formatDate: (timestamp: number) => string;
    formatDistance: (meters: number) => string;
    formatDuration: (ms: number) => string;
    formatSpeed: (mps: number) => string;
  }) => {
    const activityContext = useContext(ActivityContext);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const LONG_PRESS_DURATION = 500;
    const DRAG_THRESHOLD = 10;

    const isActive =
      activityContext?.currentActivityId === activity.id &&
      activityContext?.state !== "idle";
    const duration = isActive
      ? activityContext.getElapsedTime()
      : (activity.endTime || activity.startTime) -
        activity.startTime -
        (activity.pausedDuration || 0);

    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };

      longPressTimerRef.current = setTimeout(() => {
        if (touchStartPosRef.current) {
          onContextMenu(e, activity);
          if ("vibrate" in navigator) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (touchStartPosRef.current && longPressTimerRef.current) {
        const deltaX = Math.abs(
          e.touches[0].clientX - touchStartPosRef.current.x,
        );
        const deltaY = Math.abs(
          e.touches[0].clientY - touchStartPosRef.current.y,
        );

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          touchStartPosRef.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartPosRef.current = null;
    };

    return (
      <div
        className="setting-row"
        style={{
          cursor: "pointer",
          padding: "var(--spacing-md)",
          background: "var(--bg-subtle)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          marginBottom: "var(--spacing-sm)",
          transition: "all 0.2s",
          opacity: activity.excluded ? 0.5 : 1,
          position: "relative",
        }}
        onClick={() => onSelect(activity)}
        onContextMenu={(e) => {
          e.preventDefault();
          onContextMenu(e, activity);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "var(--bg-subtle)")
        }
      >
        <div className="setting-info">
          <div className="setting-label">
            {activity.title || "Untitled Activity"}
            {activity.excluded && (
              <span
                style={{
                  marginLeft: "8px",
                  padding: "2px 6px",
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "var(--text-secondary)",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Excluded
              </span>
            )}
            {isActive && (
              <span
                style={{
                  marginLeft: "8px",
                  padding: "2px 6px",
                  background:
                    activityContext?.state === "paused"
                      ? "var(--warning)"
                      : "var(--success)",
                  color: "var(--text-primary)",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                {activityContext?.state === "paused" ? "Paused" : "Active"}
              </span>
            )}
          </div>
          <div
            style={{
              display: "block", // Force display to override CSS that hides .setting-description
              paddingRight: "48px", // Make room for the route button
              fontSize: "12px",
              color: "var(--text-secondary)",
              lineHeight: "1.4",
              wordBreak: "break-word",
            }}
          >
            {formatDate(activity.startTime)} •{" "}
            <strong>
              {formatDistance(
                isActive ? activityContext.metrics.distance : activity.distance,
              )}
            </strong>{" "}
            • <strong>{formatDuration(duration)}</strong>
          </div>
        </div>

        {/* Route button */}
        <button
          style={{
            position: "absolute",
            top: "50%",
            right: "12px",
            transform: "translateY(-50%)",
            background: "transparent",
            border: "1px solid #60a5fa",
            borderRadius: "8px",
            padding: "4px 6px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--accent)",
            transition: "all 0.2s",
            width: "36px",
            height: "36px",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onStartRoute(activity);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(96, 165, 250, 0.1)";
            e.currentTarget.style.borderColor = "#93c5fd";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#60a5fa";
          }}
          title="Start route from activity"
        >
          <svg width="20" height="18" viewBox="0 0 24 20" fill="none">
            <path
              d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    );
  },
);

// Separate component for live metrics to isolate rerenders
const ActivityStats = React.memo(({ activity }: { activity: Activity }) => {
  const activityContext = useContext(ActivityContext);
  const { userPreferences } = usePreferences();
  const isActive =
    activityContext?.currentActivityId === activity.id &&
    activityContext?.state !== "idle";

  const units = userPreferences.units === "imperial" ? "imperial" : "metric";

  return (
    <div className="activity-detail-stats">
      <div className="activity-detail-stats-grid">
        <div className="activity-detail-stat">
          <div className="activity-detail-stat-label">Distance</div>
          <div className="activity-detail-stat-value">
            {formatDistanceWithUnit(
              isActive ? activityContext.metrics.distance : activity.distance,
              units,
            )}
          </div>
        </div>
        <div className="activity-detail-stat">
          <div className="activity-detail-stat-label">Duration</div>
          <div className="activity-detail-stat-value">
            {formatDurationShort(
              isActive
                ? activityContext.getElapsedTime()
                : (activity.endTime || activity.startTime) -
                    activity.startTime -
                    (activity.pausedDuration || 0),
            )}
          </div>
        </div>
        <div className="activity-detail-stat">
          <div className="activity-detail-stat-label">Avg Speed</div>
          <div className="activity-detail-stat-value">
            {formatSpeedWithUnit(
              isActive ? activityContext.metrics.avgSpeed : activity.avgSpeed,
              units,
            )}
          </div>
        </div>
        <div className="activity-detail-stat">
          <div className="activity-detail-stat-label">Max Speed</div>
          <div className="activity-detail-stat-value">
            {formatSpeedWithUnit(
              isActive ? activityContext.metrics.maxSpeed : activity.maxSpeed,
              units,
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

interface ActivitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActivitiesModal({ isOpen, onClose }: ActivitiesModalProps) {
  const activityContext = useContext(ActivityContext);
  const { userPreferences } = usePreferences();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null,
  );
  const [selectedActivityTrackPoints, setSelectedActivityTrackPoints] =
    useState<TrackPoint[]>([]);
  const [loadingTrackPoints, setLoadingTrackPoints] = useState(false);
  const [filter, setFilter] = useState<"all" | "week" | "month">("all");
  const [, forceUpdate] = useState({});
  const mapFitFunction = useRef<(() => void) | null>(null);
  const [isMapFitted, setIsMapFitted] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    activity: null,
    previousActivity: null,
    nextActivity: null,
    activityIndex: -1,
  });
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    visible: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [mergePreview, setMergePreview] = useState<MergePreviewState>({
    visible: false,
    firstActivity: null,
    firstTrackPoints: [],
    secondActivity: null,
    secondTrackPoints: [],
    isIntoActive: false,
    onConfirm: () => {},
  });
  const mergePreviewMapFitFunction = useRef<(() => void) | null>(null);
  const [isMergePreviewMapFitted, setIsMergePreviewMapFitted] = useState(false);

  // Memoize activity selection handler
  const handleActivitySelect = useCallback(async (activity: Activity) => {
    setSelectedActivity(activity);
    setLoadingTrackPoints(true);
    setIsEditingTitle(false); // Reset edit state when selecting new activity
    try {
      const storageService = getActivityStorageService();
      const points = await storageService.loadTrackPoints(activity.id);
      setSelectedActivityTrackPoints(points);
      debug(
        "Loaded %d track points for activity %s",
        points.length,
        activity.id,
      );

      // Debug: Check for gaps in loaded track points
      let maxGap = 0;
      for (let i = 1; i < points.length; i++) {
        const gap = points[i].timestamp - points[i - 1].timestamp;
        if (gap > maxGap) {
          maxGap = gap;
        }
        if (gap > 30000) {
          // More than 30 seconds
          debug("Found gap of %d seconds at index %d", gap / 1000, i);
        }
      }
      debug("Max gap in track points: %d seconds", maxGap / 1000);
    } catch (error) {
      debug.error("Failed to load track points:", error);
      setSelectedActivityTrackPoints([]);
    } finally {
      setLoadingTrackPoints(false);
    }
  }, []);

  // Memoize map callbacks to prevent rerenders
  const handleMapReady = useCallback((fitFn: () => void) => {
    mapFitFunction.current = fitFn;
    // Don't set fitted to true here - only when actually fitted
  }, []);

  const handleMapMoved = useCallback((isFitted: boolean) => {
    setIsMapFitted(isFitted);
  }, []);

  // Handle title editing
  const handleStartEditTitle = useCallback(() => {
    if (selectedActivity) {
      setEditedTitle(selectedActivity.title || "Untitled Activity");
      setIsEditingTitle(true);
    }
  }, [selectedActivity]);

  const handleSaveTitle = useCallback(async () => {
    if (!selectedActivity || !editedTitle.trim()) return;

    try {
      // Update the activity with new title
      const updatedActivity = {
        ...selectedActivity,
        title: editedTitle.trim(),
      };

      // Save to storage
      const storageService = getActivityStorageService();
      await storageService.saveActivity(updatedActivity);

      // Update local state
      setSelectedActivity(updatedActivity);
      setActivities((prev) =>
        prev.map((a) => (a.id === updatedActivity.id ? updatedActivity : a)),
      );

      setIsEditingTitle(false);
      debug("Updated activity title to: %s", editedTitle.trim());
    } catch (error) {
      debug.error("Failed to update activity title:", error);
    }
  }, [selectedActivity, editedTitle]);

  // Handle showing context menu
  const handleContextMenu = useCallback(
    (e: React.MouseEvent | React.TouchEvent, activity: Activity) => {
      // Filter activities based on current filter
      const now = Date.now();
      const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
      const monthAgo = now - 30 * 24 * 60 * 60 * 1000;

      let filteredActivities: Activity[];
      switch (filter) {
        case "week":
          filteredActivities = activities.filter((a) => a.startTime > weekAgo);
          break;
        case "month":
          filteredActivities = activities.filter((a) => a.startTime > monthAgo);
          break;
        default:
          filteredActivities = activities;
      }

      const index = filteredActivities.findIndex((a) => a.id === activity.id);
      // Activities are sorted newest first:
      // - Previous activity in time is at index + 1 (older)
      // - Next activity in time is at index - 1 (newer)
      const previousActivity =
        index < filteredActivities.length - 1
          ? filteredActivities[index + 1]
          : null;
      const nextActivity = index > 0 ? filteredActivities[index - 1] : null;

      let x: number, y: number;
      if ("touches" in e) {
        // Touch event
        x = e.touches[0]?.clientX || 0;
        y = e.touches[0]?.clientY || 0;
      } else {
        // Mouse event
        x = e.clientX;
        y = e.clientY;
      }

      // Adjust position to keep menu within viewport
      const menuWidth = 250; // Approximate menu width
      // Calculate menu height based on available merge options
      let menuHeight = 140; // Base height with title, export, delete
      if (previousActivity) menuHeight += 40; // Add height for merge with previous
      if (nextActivity) menuHeight += 40; // Add height for merge with next

      // Check right edge
      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 10;
      }

      // Check bottom edge
      if (y + menuHeight > window.innerHeight) {
        y = window.innerHeight - menuHeight - 10;
      }

      // Check left edge
      if (x < 10) {
        x = 10;
      }

      // Check top edge
      if (y < 10) {
        y = 10;
      }

      setContextMenu({
        visible: true,
        x,
        y,
        activity,
        previousActivity,
        nextActivity,
        activityIndex: index,
      });
    },
    [activities, filter],
  );

  // Handle merging with next activity
  const handleMergeWithNext = useCallback(async () => {
    if (!contextMenu.activity || !contextMenu.nextActivity) return;

    try {
      const storageService = getActivityStorageService();

      // Load track points for both activities for preview
      const currentPoints = await storageService.loadTrackPoints(
        contextMenu.activity.id,
      );
      const nextPoints = await storageService.loadTrackPoints(
        contextMenu.nextActivity.id,
      );

      // Show merge preview
      mergePreviewMapFitFunction.current = null;
      setIsMergePreviewMapFitted(false);
      setMergePreview({
        visible: true,
        firstActivity: contextMenu.activity, // current is older
        firstTrackPoints: currentPoints,
        secondActivity: contextMenu.nextActivity, // next is newer
        secondTrackPoints: nextPoints,
        isIntoActive: false,
        onConfirm: async () => {
          setMergePreview({ ...mergePreview, visible: false });

          // Re-check after closing preview
          const activity = contextMenu.activity;
          const nextActivity = contextMenu.nextActivity;
          if (!activity || !nextActivity) return;

          try {
            const storageService = getActivityStorageService();

            // Load track points for both activities BEFORE any modifications
            debug("Loading track points for merge with next...");
            const currentTrackPoints = await storageService.loadTrackPoints(
              activity.id,
            );
            const nextTrackPoints = await storageService.loadTrackPoints(
              nextActivity.id,
            );

            debug(
              "Loaded track points - current activity: %d points, next activity: %d points",
              currentTrackPoints.length,
              nextTrackPoints.length,
            );

            if (
              currentTrackPoints.length === 0 ||
              nextTrackPoints.length === 0
            ) {
              debug.error("One or both activities have no track points!");
              debug(
                "Current activity %s: %d points",
                activity.id,
                currentTrackPoints.length,
              );
              debug(
                "Next activity %s: %d points",
                nextActivity.id,
                nextTrackPoints.length,
              );
              throw new Error("Cannot merge activities without track points");
            }

            // Verify track points are valid
            const invalidCurrent = currentTrackPoints.filter(
              (p) => !p.position || !p.timestamp,
            );
            const invalidNext = nextTrackPoints.filter(
              (p) => !p.position || !p.timestamp,
            );
            if (invalidCurrent.length > 0 || invalidNext.length > 0) {
              debug.error(
                "Found invalid track points! Current: %d invalid, Next: %d invalid",
                invalidCurrent.length,
                invalidNext.length,
              );
            }

            // Create merged activity - using the earliest start time and latest end time
            const mergedActivity: Activity = {
              ...activity,
              // Combine title if different
              title:
                activity.title === nextActivity.title
                  ? activity.title
                  : `${activity.title || "Activity"} + ${nextActivity.title || "Activity"}`,
              // Use earliest start time (current activity is older)
              startTime: activity.startTime,
              // Use latest end time (next activity is newer)
              endTime: nextActivity.endTime || nextActivity.startTime,
              // Sum up distances
              distance: (activity.distance || 0) + (nextActivity.distance || 0),
              // Sum up paused durations
              pausedDuration:
                (activity.pausedDuration || 0) +
                (nextActivity.pausedDuration || 0),
              // Calculate weighted average speed
              avgSpeed:
                (activity.avgSpeed * (activity.distance || 0) +
                  nextActivity.avgSpeed * (nextActivity.distance || 0)) /
                ((activity.distance || 0) + (nextActivity.distance || 0) || 1),
              // Take the maximum speed from either activity
              maxSpeed: Math.max(
                activity.maxSpeed || 0,
                nextActivity.maxSpeed || 0,
              ),
            };

            // Create a new merged activity with a new ID (don't delete originals)
            const mergedActivityWithNewId = {
              ...mergedActivity,
              id: `merged_${Date.now()}`,
              title:
                mergedActivity.title ||
                `Merged: ${activity.title || "Activity"} + ${nextActivity.title || "Activity"}`,
            };

            // Save the new merged activity (keeping originals)
            debug(
              "Creating new merged activity with ID: %s",
              mergedActivityWithNewId.id,
            );
            await storageService.saveActivity(mergedActivityWithNewId);

            // Exclude the original activities from stats (since they're now represented by the merged one)
            const excludedCurrent = { ...activity, excluded: true };
            const excludedNext = { ...nextActivity, excluded: true };

            await storageService.saveActivity(excludedCurrent);
            await storageService.saveActivity(excludedNext);

            debug(
              "Original activities excluded from stats: %s and %s",
              activity.id,
              nextActivity.id,
            );

            // Save all track points in chronological order
            // Current activity is older, its points come first
            const allTrackPoints = [...currentTrackPoints, ...nextTrackPoints];

            // Debug: Check the gap between activities
            if (currentTrackPoints.length > 0 && nextTrackPoints.length > 0) {
              const lastCurrPoint =
                currentTrackPoints[currentTrackPoints.length - 1];
              const firstNextPoint = nextTrackPoints[0];
              const gap = firstNextPoint.timestamp - lastCurrPoint.timestamp;
              debug(
                "Gap between merged activities: %d ms (%d seconds)",
                gap,
                gap / 1000,
              );
              debug(
                "Last point of current: %s",
                new Date(lastCurrPoint.timestamp).toISOString(),
              );
              debug(
                "First point of next: %s",
                new Date(firstNextPoint.timestamp).toISOString(),
              );
            }

            debug(
              "Saving %d total track points to merged activity %s",
              allTrackPoints.length,
              mergedActivityWithNewId.id,
            );
            await storageService.saveTrackPoints(
              mergedActivityWithNewId.id,
              allTrackPoints,
            );

            // Verify the save worked
            const verifyPoints = await storageService.loadTrackPoints(
              mergedActivityWithNewId.id,
            );
            debug(
              "Verification after save: merged activity has %d track points",
              verifyPoints.length,
            );
            if (verifyPoints.length !== allTrackPoints.length) {
              debug.error(
                "Track points mismatch! Expected %d, got %d",
                allTrackPoints.length,
                verifyPoints.length,
              );
            }

            // If we were viewing one of the merged activities, close the detail view
            if (
              selectedActivity &&
              (selectedActivity.id === activity.id ||
                selectedActivity.id === nextActivity.id)
            ) {
              setSelectedActivity(null);
              setSelectedActivityTrackPoints([]);
              setIsMapFitted(false);
              mapFitFunction.current = null;
            }

            // Force reload activities immediately
            const loadedActivities =
              (await activityContext?.loadHistoricalActivities()) || [];
            setActivities(loadedActivities);
            debug(
              "Activities reloaded after merge: %d activities",
              loadedActivities.length,
            );
            debug("Merged activities %s and %s", activity.id, nextActivity.id);
          } catch (error) {
            debug.error("Failed to merge activities:", error);
          } finally {
            setContextMenu({ ...contextMenu, visible: false });
          }
        },
      });
    } catch (error) {
      debug.error("Failed to load track points for merge preview:", error);
    }

    setContextMenu({ ...contextMenu, visible: false });
  }, [contextMenu, activityContext, confirmation, mergePreview]);

  // Close context menu when modal closes
  useEffect(() => {
    if (!isOpen && contextMenu.visible) {
      setContextMenu({ ...contextMenu, visible: false });
    }
  }, [isOpen]);

  useEffect(() => {
    // Only load when modal opens (transition from closed to open)
    if (!isOpen || !activityContext) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const loadedActivities =
          await activityContext.loadHistoricalActivities();
        if (!cancelled) {
          setActivities(loadedActivities);
          debug("Loaded %d activities", loadedActivities.length);
        }
      } catch (error) {
        if (!cancelled) {
          debug.error("Failed to load activities:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen]); // Only trigger on isOpen change - context is now properly memoized

  // Update every second if there's an active activity
  useEffect(() => {
    if (!isOpen || !activityContext || activityContext.state === "idle") return;

    const interval = setInterval(() => {
      forceUpdate({});
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, activityContext?.state]);

  const handleToggleExclude = useCallback(
    async (activity: Activity) => {
      if (!activityContext) return;

      try {
        const storageService = getActivityStorageService();

        // Toggle the excluded flag
        const updatedActivity = {
          ...activity,
          excluded: !activity.excluded,
        };

        // Save the updated activity
        await storageService.saveActivity(updatedActivity);

        // Reload activities
        const loadedActivities =
          await activityContext.loadHistoricalActivities();
        setActivities(loadedActivities);

        debug(
          "Toggled exclude for activity %s to %s",
          activity.id,
          !activity.excluded,
        );
      } catch (error) {
        debug.error("Failed to toggle exclude status:", error);
      }
    },
    [activityContext],
  );

  const handleDelete = useCallback(
    async (id: string, activityTitle?: string) => {
      if (!activityContext) return;

      setConfirmation({
        visible: true,
        title: "Delete Activity",
        message: `Are you sure you want to delete "${activityTitle || "this activity"}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        isDangerous: true,
        onConfirm: async () => {
          setConfirmation({ ...confirmation, visible: false });
          try {
            await activityContext.deleteActivity(id);
            // Reload activities
            const loadedActivities =
              await activityContext.loadHistoricalActivities();
            setActivities(loadedActivities);
          } catch (error) {
            debug.error("Failed to delete activity:", error);
          }
        },
      });
    },
    [activityContext, confirmation],
  );

  const startRouteFromActivity = async (activity: Activity) => {
    try {
      const storageService = getActivityStorageService();
      const routeService = getRouteService();

      // Load track points for the activity
      debug("Loading track points for activity %s", activity.id);
      const trackPoints = await storageService.loadTrackPoints(activity.id);

      if (!trackPoints || trackPoints.length === 0) {
        setConfirmation({
          visible: true,
          title: "No Track Data",
          message: "This activity has no track data to convert to a route.",
          confirmText: "OK",
          cancelText: "",
          onConfirm: () => {
            setConfirmation({ ...confirmation, visible: false });
          },
        });
        return;
      }

      debug(
        "Converting activity to route with %d track points",
        trackPoints.length,
      );

      // Generate the route geometry from track points (don't save, just create)
      const route = await routeService.createRouteFromActivity(
        activity,
        trackPoints,
        false,
      );

      // Display the route on the map and zoom to fit
      const mapService = window.__bikedash_maps?.["main"] as
        | MapService
        | undefined;
      if (!mapService) {
        throw new Error("Map service not initialized");
      }
      const overpassWorker = getOverpassWorkerService();

      // Clear search results and set map to free mode
      mapService.clearSearchResults();
      mapService.setMode("free");
      mapService.setRotation(0);

      // Display the route geometry on the map (this will zoom to fit)
      if (route.geometry && route.geometry.length > 0) {
        mapService.displaySelectedFeature(route.geometry, overpassWorker);
      }

      // Start the route immediately
      routeService.startRoute({
        routeName: route.name,
        routeId: route.id,
        totalDistance: route.totalDistance || 0,
        estimatedTime: route.estimatedTime,
        geometry: route.geometry,
        centerLat: route.centerLat,
        centerLon: route.centerLon,
      });

      debug("Started route from activity %s", activity.id);

      // Close the activities modal to show the route
      onClose();
    } catch (error) {
      debug.error("Failed to start route from activity:", error);
      console.error("Failed to start route from activity:", error);
      setConfirmation({
        visible: true,
        title: "Error",
        message: "Failed to start route from activity. Please try again.",
        confirmText: "OK",
        cancelText: "",
        onConfirm: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
    }
  };

  const handleStartRoute = useCallback(
    async (activity: Activity) => {
      if (!activity) return;

      const routeService = getRouteService();
      const currentRouteState = routeService.getState();

      // Check if there's an active route
      if (currentRouteState.isActive) {
        setConfirmation({
          visible: true,
          title: "Replace Active Route?",
          message: `You have an active route "${currentRouteState.routeName}". Do you want to replace it with a route from this activity?`,
          confirmText: "Replace Route",
          cancelText: "Cancel",
          onConfirm: async () => {
            setConfirmation({ ...confirmation, visible: false });
            await startRouteFromActivity(activity);
          },
        });
      } else {
        await startRouteFromActivity(activity);
      }
    },
    [confirmation],
  );

  const units = userPreferences.units === "imperial" ? "imperial" : "metric";

  const formatDistance = useCallback(
    (meters: number) => formatDistanceWithUnit(meters, units),
    [units],
  );

  const formatSpeed = useCallback(
    (mps: number) => formatSpeedWithUnit(mps, units),
    [units],
  );

  const formatDuration = useCallback(
    (ms: number) => formatDurationShort(ms),
    [],
  );

  const formatDate = useCallback(
    (timestamp: number) => formatDateTime(timestamp),
    [],
  );

  const getFilteredActivities = useMemo(() => {
    const now = Date.now();
    const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
    const monthAgo = now - 30 * 24 * 60 * 60 * 1000;

    switch (filter) {
      case "week":
        return activities.filter((a) => a.startTime > weekAgo);
      case "month":
        return activities.filter((a) => a.startTime > monthAgo);
      default:
        return activities;
    }
  }, [activities, filter]);

  // Memoize the routes array to prevent constant re-renders
  // MUST be before any conditional returns for hooks to work properly
  const activityRoutes = useMemo(() => {
    if (
      !selectedActivity ||
      loadingTrackPoints ||
      selectedActivityTrackPoints.length === 0
    ) {
      return [];
    }

    const timestamps = selectedActivityTrackPoints.map((p) => p.timestamp);

    // Debug: Check timestamps being passed to map
    if (timestamps.length > 1) {
      debug(
        "Route timestamps - first: %s, last: %s, count: %d",
        new Date(timestamps[0]).toISOString(),
        new Date(timestamps[timestamps.length - 1]).toISOString(),
        timestamps.length,
      );
    }

    // Filter out any track points with invalid positions or 0,0 coordinates
    const validTrackPoints = selectedActivityTrackPoints.filter(
      (p) =>
        p.position.lat !== undefined &&
        p.position.lng !== undefined &&
        !(p.position.lat === 0 && p.position.lng === 0),
    );

    return [
      {
        id: selectedActivity.id,
        positions: validTrackPoints.map((p) => p.position),
        timestamps: validTrackPoints.map((p) => p.timestamp),
        type: "track" as const,
        gapThreshold: 10000, // 10 seconds gap detection for pauses
      },
    ];
  }, [selectedActivity?.id, loadingTrackPoints, selectedActivityTrackPoints]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        // If activity detail is open, close that first
        if (selectedActivity) {
          setSelectedActivity(null);
          setSelectedActivityTrackPoints([]);
          mapFitFunction.current = null;
          setIsMapFitted(false);
        } else {
          // Only close main modal if no detail view is open
          onClose();
        }
      }}
    >
      <div
        className="modal settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>
          <span>×</span>
        </button>

        <div className="settings-tabs">
          <button
            className={`tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            Activities ({getFilteredActivities.length})
          </button>
          <button
            className={`tab ${filter === "week" ? "active" : ""}`}
            onClick={() => setFilter("week")}
          >
            This Week
          </button>
          <button
            className={`tab ${filter === "month" ? "active" : ""}`}
            onClick={() => setFilter("month")}
          >
            This Month
          </button>
        </div>

        <div className="settings-content">
          <div className="settings-section">
            {/* Summary stats */}
            <div
              className="status-grid"
              style={{ marginBottom: "var(--spacing-lg)" }}
            >
              <TotalStats
                activities={getFilteredActivities}
                formatDistance={formatDistance}
                formatDuration={formatDuration}
              />
            </div>

            {loading ? (
              <div
                className="text-center text-muted"
                style={{ padding: "40px" }}
              >
                Loading activities...
              </div>
            ) : getFilteredActivities.length === 0 ? (
              <div
                className="text-center text-muted"
                style={{ padding: "40px" }}
              >
                No activities found
              </div>
            ) : (
              getFilteredActivities.map((activity) => (
                <ActivityListItem
                  key={activity.id}
                  activity={activity}
                  onSelect={handleActivitySelect}
                  onDelete={handleDelete}
                  onContextMenu={handleContextMenu}
                  onStartRoute={handleStartRoute}
                  formatDate={formatDate}
                  formatDistance={formatDistance}
                  formatDuration={formatDuration}
                  formatSpeed={formatSpeed}
                />
              ))
            )}
          </div>
        </div>

        {selectedActivity && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
            onClick={(e) => {
              // Prevent closing the main modal
              e.stopPropagation();
              // Only close the activity detail view
              setSelectedActivity(null);
              setSelectedActivityTrackPoints([]);
              mapFitFunction.current = null;
              setIsMapFitted(true);
            }}
          >
            <div
              className="modal activity-detail-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "100%",
                width: "100%",
                height: "100%",
                padding: "0 !important" as any,
                margin: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* Full background map - positioned absolute to ignore any container padding */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                {/* Map without children to prevent rerenders */}
                <ActivityMap
                  routes={activityRoutes}
                  onMapReady={handleMapReady}
                  onMapMoved={handleMapMoved}
                />

                {/* Overlay content positioned absolutely over the map */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    zIndex: 1,
                  }}
                >
                  {/* Fit button on the LEFT */}
                  <button
                    onClick={() => {
                      if (mapFitFunction.current) {
                        mapFitFunction.current();
                        setIsMapFitted(true);
                      }
                    }}
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "20px",
                      width: "40px",
                      height: "40px",
                      background: "rgba(0, 0, 0, 0.7)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      color: isMapFitted
                        ? "#3e6ae1"
                        : "rgba(255, 255, 255, 0.4)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}
                    title={isMapFitted ? "Map is fitted" : "Fit map to route"}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {/* Corner brackets like fullscreen icon */}
                      <path d="M4 8V4h4M4 16v4h4M16 4h4v4M16 20h4v-4" />
                      {/* Checkmark in center when fitted */}
                      {isMapFitted && (
                        <path
                          d="M8 12l3 3l5-6"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                    </svg>
                  </button>

                  {/* Close button on the RIGHT */}
                  <button
                    onClick={() => {
                      setSelectedActivity(null);
                      setSelectedActivityTrackPoints([]);
                      setIsMapFitted(false);
                      setIsEditingTitle(false);
                    }}
                    style={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      width: "40px",
                      height: "40px",
                      background: "rgba(0, 0, 0, 0.7)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      color: "var(--text-primary)",
                      fontSize: "20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                      e.currentTarget.style.background = "rgba(0, 0, 0, 0.8)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
                    }}
                  >
                    ×
                  </button>

                  {/* Header with title - using CSS class */}
                  <div className="activity-detail-header">
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {isEditingTitle ? (
                          <h2 style={{ margin: 0, flex: 1 }}>
                            <input
                              type="text"
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveTitle();
                                }
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "var(--accent)",
                                fontSize: "inherit",
                                fontWeight: "inherit",
                                fontFamily: "inherit",
                                lineHeight: "inherit",
                                padding: 0,
                                margin: 0,
                                outline: "none",
                                width: "100%",
                              }}
                              autoFocus
                            />
                          </h2>
                        ) : (
                          <h2 style={{ margin: 0, flex: 1 }}>
                            {selectedActivity.title || "Untitled Activity"}
                          </h2>
                        )}
                        <button
                          onClick={
                            isEditingTitle
                              ? handleSaveTitle
                              : handleStartEditTitle
                          }
                          style={{
                            background: "transparent",
                            border: "none",
                            color: isEditingTitle
                              ? "#3e6ae1"
                              : "rgba(255, 255, 255, 0.4)",
                            padding: "4px",
                            cursor: "pointer",
                            transition: "color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = isEditingTitle
                              ? "#5c7cfa"
                              : "rgba(255, 255, 255, 0.7)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = isEditingTitle
                              ? "#3e6ae1"
                              : "rgba(255, 255, 255, 0.4)";
                          }}
                          title={isEditingTitle ? "Save (Enter)" : "Edit name"}
                        >
                          {isEditingTitle ? "Save" : "Edit"}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "var(--text-secondary)",
                          marginTop: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {formatDate(selectedActivity.startTime)}
                        {activityContext?.currentActivityId ===
                          selectedActivity.id &&
                          activityContext?.state !== "idle" && (
                            <span
                              style={{
                                padding: "2px 6px",
                                background:
                                  activityContext.state === "paused"
                                    ? "var(--warning)"
                                    : "var(--success)",
                                color: "var(--text-primary)",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              {activityContext.state === "paused"
                                ? "Paused"
                                : "Active"}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Stats overlay at bottom - isolated component to prevent map rerenders */}
                  <ActivityStats activity={selectedActivity} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Context Menu */}
        <ActivityContextMenu
          visible={contextMenu.visible}
          x={contextMenu.x}
          y={contextMenu.y}
          activity={contextMenu.activity}
          nextActivity={contextMenu.nextActivity}
          currentActivityId={activityContext?.currentActivityId || undefined}
          onClose={() => setContextMenu({ ...contextMenu, visible: false })}
          onViewDetails={handleActivitySelect}
          onToggleExclude={handleToggleExclude}
          onMergeWithNext={async (activity, nextActivity) => {
            // Check if next activity is the current active activity
            if (nextActivity.id === activityContext?.currentActivityId) {
              // Merge into active recording - show preview
              const activeActivityId = activityContext?.currentActivityId;
              if (!activity || !activeActivityId) return;

              try {
                const storageService = getActivityStorageService();

                // Load track points for preview
                const activityPoints = await storageService.loadTrackPoints(
                  activity.id,
                );
                const activePoints =
                  await storageService.loadTrackPoints(activeActivityId);

                // Load the active activity metadata
                const activeActivity =
                  await storageService.loadActivity(activeActivityId);

                if (!activeActivity) {
                  debug.error("Could not load active activity metadata");
                  return;
                }

                // Show merge preview with active activity
                mergePreviewMapFitFunction.current = null;
                setIsMergePreviewMapFitted(false);
                setMergePreview({
                  visible: true,
                  firstActivity: activity, // activity to merge (older)
                  firstTrackPoints: activityPoints,
                  secondActivity: activeActivity, // active recording (newer)
                  secondTrackPoints: activePoints,
                  isIntoActive: true,
                  onConfirm: async () => {
                    setMergePreview({ ...mergePreview, visible: false });

                    try {
                      const storageService = getActivityStorageService();

                      // Load track points from the activity to merge
                      debug("Loading track points for merge into active...");
                      const activityToMergePoints =
                        await storageService.loadTrackPoints(activity.id);
                      const currentActivityPoints =
                        await storageService.loadTrackPoints(activeActivityId);

                      debug(
                        "Loaded track points - activity to merge: %d points, current active: %d points",
                        activityToMergePoints.length,
                        currentActivityPoints.length,
                      );

                      if (activityToMergePoints.length === 0) {
                        debug.error("Activity to merge has no track points!");
                        return;
                      }

                      // Merge track points (activity to merge first, then current)
                      const mergedPoints = [
                        ...activityToMergePoints,
                        ...currentActivityPoints,
                      ];

                      debug(
                        "Saving %d total track points to active recording",
                        mergedPoints.length,
                      );
                      await storageService.saveTrackPoints(
                        activeActivityId,
                        mergedPoints,
                      );

                      // Load the full activity to preserve all fields and get metrics
                      const fullActivity = await storageService.loadActivity(
                        activity.id,
                      );
                      if (fullActivity && activityContext) {
                        // Update the activity context's internal metrics
                        activityContext.mergeActivityMetrics(
                          fullActivity.distance || 0,
                          fullActivity.startTime,
                          fullActivity.pausedDuration || 0,
                        );

                        // Mark the merged activity as excluded and save it back (don't delete!)
                        const excludedActivity = {
                          ...fullActivity,
                          excluded: true,
                        };
                        await storageService.saveActivity(excludedActivity);
                        debug(
                          "Marked activity %s as excluded from stats",
                          activity.id,
                        );
                      }

                      // If we were viewing the merged activity, close the detail view
                      if (
                        selectedActivity &&
                        selectedActivity.id === activity.id
                      ) {
                        setSelectedActivity(null);
                        setSelectedActivityTrackPoints([]);
                        setIsMapFitted(false);
                        mapFitFunction.current = null;
                      }

                      // Reload activities
                      const loadedActivities =
                        (await activityContext?.loadHistoricalActivities()) ||
                        [];
                      setActivities(loadedActivities);

                      debug(
                        "Successfully merged %s into active recording",
                        activity.id,
                      );
                    } catch (error) {
                      debug.error(
                        "Failed to merge into active recording:",
                        error,
                      );
                    }
                  },
                });
              } catch (error) {
                debug.error("Failed to load data for merge preview:", error);
              }
            } else {
              // Normal merge with next
              handleMergeWithNext();
            }
          }}
          onDelete={handleDelete}
          onSaveAsRoute={handleStartRoute}
        />

        {/* Confirmation Modal */}
        <ConfirmationDialog
          visible={confirmation.visible}
          title={confirmation.title}
          message={confirmation.message}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          isDangerous={confirmation.isDangerous}
          onConfirm={confirmation.onConfirm}
          onCancel={() => setConfirmation({ ...confirmation, visible: false })}
        />

        {/* Merge Preview Modal */}
        {mergePreview.visible &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100002,
              }}
              onClick={() =>
                setMergePreview({ ...mergePreview, visible: false })
              }
            >
              <div
                className="modal merge-preview-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: "90%",
                  width: "90%",
                  height: "85%",
                  padding: "0",
                  margin: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  background: "var(--bg-primary)",
                  borderRadius: "12px",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "20px",
                    borderBottom: "1px solid var(--border)",
                    background: "rgba(0, 0, 0, 0.5)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <h2 style={{ margin: 0, marginBottom: "8px" }}>
                    {mergePreview.isIntoActive
                      ? "Merge into Active Recording"
                      : "Merge Activities Preview"}
                  </h2>
                  <div
                    style={{ color: "var(--text-secondary)", fontSize: "14px" }}
                  >
                    <span style={{ color: "#9333ea", fontWeight: "bold" }}>
                      ●
                    </span>{" "}
                    {mergePreview.firstActivity?.title || "First Activity"} (
                    {formatDate(mergePreview.firstActivity?.startTime || 0)})
                    {" → "}
                    <span style={{ color: "#3e6ae1", fontWeight: "bold" }}>
                      ●
                    </span>{" "}
                    {mergePreview.secondActivity?.title || "Second Activity"} (
                    {formatDate(mergePreview.secondActivity?.startTime || 0)})
                  </div>
                </div>

                {/* Map */}
                <div style={{ flex: 1, position: "relative" }}>
                  <EmbeddedMapView
                    routes={
                      [
                        // First activity (older) in purple/route color - filter out invalid positions and 0,0
                        mergePreview.firstTrackPoints.length > 0
                          ? {
                              id: `preview_first_${mergePreview.firstActivity?.id}`,
                              positions: mergePreview.firstTrackPoints
                                .filter(
                                  (tp) =>
                                    tp.position.lat !== undefined &&
                                    tp.position.lng !== undefined &&
                                    !(
                                      tp.position.lat === 0 &&
                                      tp.position.lng === 0
                                    ),
                                )
                                .map((tp) => tp.position),
                              timestamps: mergePreview.firstTrackPoints
                                .filter(
                                  (tp) =>
                                    tp.position.lat !== undefined &&
                                    tp.position.lng !== undefined &&
                                    !(
                                      tp.position.lat === 0 &&
                                      tp.position.lng === 0
                                    ),
                                )
                                .map((tp) => tp.timestamp),
                              type: "route" as const,
                              color: "#9333ea",
                              gapThreshold: 10000,
                            }
                          : null,
                        // Second activity (newer) in blue/track color - filter out invalid positions and 0,0
                        mergePreview.secondTrackPoints.length > 0
                          ? {
                              id: `preview_second_${mergePreview.secondActivity?.id}`,
                              positions: mergePreview.secondTrackPoints
                                .filter(
                                  (tp) =>
                                    tp.position.lat !== undefined &&
                                    tp.position.lng !== undefined &&
                                    !(
                                      tp.position.lat === 0 &&
                                      tp.position.lng === 0
                                    ),
                                )
                                .map((tp) => tp.position),
                              timestamps: mergePreview.secondTrackPoints
                                .filter(
                                  (tp) =>
                                    tp.position.lat !== undefined &&
                                    tp.position.lng !== undefined &&
                                    !(
                                      tp.position.lat === 0 &&
                                      tp.position.lng === 0
                                    ),
                                )
                                .map((tp) => tp.timestamp),
                              type: "track" as const,
                              color: "#3e6ae1",
                              gapThreshold: 10000,
                            }
                          : null,
                      ].filter(Boolean) as RouteData[]
                    }
                    fitToContent={true}
                    interactive={true}
                    fitPadding={[40, 40, 40, 40]} // Simple uniform padding - no overlay controls in merge preview
                    onMapReady={(fitFn) => {
                      mergePreviewMapFitFunction.current = fitFn;
                      setIsMergePreviewMapFitted(false);
                    }}
                    onMapMoved={(isFitted) => {
                      setIsMergePreviewMapFitted(isFitted);
                    }}
                  />

                  {/* Fit button for merge preview map */}
                  <button
                    onClick={() => {
                      if (mergePreviewMapFitFunction.current) {
                        mergePreviewMapFitFunction.current();
                        setIsMergePreviewMapFitted(true);
                      }
                    }}
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "20px",
                      width: "40px",
                      height: "40px",
                      background: "rgba(0, 0, 0, 0.7)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      color: isMergePreviewMapFitted
                        ? "var(--success)"
                        : "var(--text-primary)",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      zIndex: 10,
                    }}
                    title={
                      isMergePreviewMapFitted
                        ? "Map is fitted"
                        : "Fit map to routes"
                    }
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {/* Corner brackets like fullscreen icon */}
                      <path d="M4 8V4h4M4 16v4h4M16 4h4v4M16 20h4v-4" />
                      {/* Checkmark in center when fitted */}
                      {isMergePreviewMapFitted && (
                        <path
                          d="M8 12l3 3l5-6"
                          stroke="var(--success)"
                          strokeWidth="2.5"
                        />
                      )}
                    </svg>
                  </button>
                </div>

                {/* Footer with stats and buttons */}
                <div
                  style={{
                    padding: "20px",
                    borderTop: "1px solid var(--border)",
                    background: "rgba(0, 0, 0, 0.5)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "20px",
                      marginBottom: "20px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginBottom: "4px",
                        }}
                      >
                        Combined Distance
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {formatDistance(
                          (mergePreview.firstActivity?.distance || 0) +
                            (mergePreview.secondActivity?.distance || 0),
                        )}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginBottom: "4px",
                        }}
                      >
                        Combined Duration
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {formatDuration(
                          (mergePreview.firstActivity?.endTime ||
                            mergePreview.firstActivity?.startTime ||
                            0) -
                            (mergePreview.firstActivity?.startTime || 0) -
                            (mergePreview.firstActivity?.pausedDuration || 0) +
                            ((mergePreview.secondActivity?.endTime ||
                              mergePreview.secondActivity?.startTime ||
                              0) -
                              (mergePreview.secondActivity?.startTime || 0) -
                              (mergePreview.secondActivity?.pausedDuration ||
                                0)),
                        )}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginBottom: "4px",
                        }}
                      >
                        Track Points
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {mergePreview.firstTrackPoints.length +
                          mergePreview.secondTrackPoints.length}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() =>
                        setMergePreview({ ...mergePreview, visible: false })
                      }
                      style={{
                        padding: "10px 20px",
                        background: "var(--bg-subtle)",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        color: "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        mergePreview.onConfirm();
                      }}
                      style={{
                        padding: "10px 20px",
                        background: mergePreview.isIntoActive
                          ? "var(--warning)"
                          : "var(--primary)",
                        border: "none",
                        borderRadius: "6px",
                        color: "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {mergePreview.isIntoActive
                        ? "Merge into Active"
                        : "Confirm Merge"}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}
